/**
 * 全局API调用管理器
 * 确保同一时间只有一个接口调用，防止重复点击和并发请求
 */

class ApiCallManager {
  constructor() {
    // 正在进行的请求映射表
    this.pendingRequests = new Map();
    // 全局锁标志
    this.isGlobalLocked = false;
    // 当前正在进行的请求数量
    this.currentRequestsCount = 0;
    // 请求历史记录（用于调试）
    this.requestHistory = [];
  }

  /**
   * 生成请求的唯一标识符
   * @param {string} url 请求URL
   * @param {string} method 请求方法
   * @param {any} data 请求数据
   * @returns {string} 请求标识符
   */
  generateRequestKey(url, method, data) {
    // 对于相同的URL和方法，生成相同的key（防止重复提交）
    // 但对于不同的数据，生成不同的key（允许不同参数的请求）
    const baseKey = `${method.toUpperCase()}-${url}`;

    // 如果是FormData，提取关键字段
    if (data instanceof FormData) {
      const keys = [];
      for (let [key, value] of data.entries()) {
        keys.push(`${key}=${typeof value === 'string' ? value : '[object]'}`);
      }
      return keys.length > 0 ? `${baseKey}-${keys.join('&')}` : baseKey;
    }

    // 对于其他类型的数据，简化处理
    const dataStr = data ? JSON.stringify(data) : '';
    return dataStr ? `${baseKey}-${this.hashCode(dataStr)}` : baseKey;
  }

  /**
   * 简单的哈希函数，用于缩短数据标识
   */
  hashCode(str) {
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 8);
  }

  /**
   * 检查是否可以发起请求
   * @param {string} requestKey 请求标识符
   * @returns {boolean} 是否可以发起请求
   */
  canMakeRequest(requestKey) {
    // 如果全局锁定，则不允许任何新请求
    if (this.isGlobalLocked) {
      return false;
    }

    // 如果已有相同请求在进行，则不允许
    if (this.pendingRequests.has(requestKey)) {
      return false;
    }

    return true;
  }

  /**
   * 添加请求到待处理列表
   * @param {string} requestKey 请求标识符
   * @param {Promise} requestPromise 请求Promise
   */
  addPendingRequest(requestKey, requestPromise) {
    const startTime = Date.now();

    // 添加到请求映射表
    this.pendingRequests.set(requestKey, {
      promise: requestPromise,
      startTime: startTime,
      key: requestKey
    });

    this.currentRequestsCount++;

    // 如果是第一个请求，设置全局锁
    if (this.currentRequestsCount === 1) {
      this.isGlobalLocked = true;
    }

    // 添加到历史记录
    this.requestHistory.push({
      key: requestKey,
      startTime: startTime,
      status: 'pending'
    });

    // 保持历史记录在合理大小
    if (this.requestHistory.length > 100) {
      this.requestHistory = this.requestHistory.slice(-50);
    }

    // 请求完成后清理
    const cleanup = (status, error = null) => {
      const endTime = Date.now();
      const duration = endTime - startTime;

      // 从待处理列表中移除
      this.pendingRequests.delete(requestKey);
      this.currentRequestsCount--;

      // 更新历史记录
      const historyItem = this.requestHistory.find(item =>
        item.key === requestKey && item.startTime === startTime
      );
      if (historyItem) {
        historyItem.status = status;
        historyItem.endTime = endTime;
        historyItem.duration = duration;
        historyItem.error = error;
      }


      // 如果没有待处理的请求了，解除全局锁
      if (this.currentRequestsCount <= 0) {
        this.isGlobalLocked = false;
        this.currentRequestsCount = 0;
      }
    };

    // 处理请求成功
    requestPromise.then((result) => {
      cleanup('completed');
      return result;
    }).catch((error) => {
      cleanup('failed', error);
      throw error;
    });
  }

  /**
   * 取消所有待处理的请求
   */
  cancelAllRequests() {
    this.pendingRequests.clear();
    this.currentRequestsCount = 0;
    this.isGlobalLocked = false;
  }

  /**
   * 获取当前状态信息
   * @returns {object} 状态信息
   */
  getStatus() {
    return {
      isLocked: this.isGlobalLocked,
      pendingCount: this.currentRequestsCount,
      pendingRequests: Array.from(this.pendingRequests.keys()),
      requestHistory: this.requestHistory.slice(-10) // 最近10条记录
    };
  }

  /**
   * 强制解锁（紧急情况使用）
   */
  forceUnlock() {
    console.warn('⚠️ 强制解除API调用锁定');
    this.cancelAllRequests();
  }

  /**
   * 获取请求历史统计
   */
  getStats() {
    const completed = this.requestHistory.filter(r => r.status === 'completed');
    const failed = this.requestHistory.filter(r => r.status === 'failed');
    const avgDuration = completed.length > 0
      ? completed.reduce((sum, r) => sum + (r.duration || 0), 0) / completed.length
      : 0;

    return {
      total: this.requestHistory.length,
      completed: completed.length,
      failed: failed.length,
      pending: this.currentRequestsCount,
      averageDuration: Math.round(avgDuration)
    };
  }
}

// 创建全局单例
const apiCallManager = new ApiCallManager();

export default apiCallManager;


// WEBPACK FOOTER //
// ./src/assets/js/apiCallManager.js